import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

export function passwordValidator(control: FormControl): ValidationErrors | null {
  if (control && (control.value === '' || control.value === null)) return null;

  const hasNumberSpecialRegex = /^(?!.*\s)(?=.*\d)(?=.*[^a-zA-Z0-9\s]).*$/g;
  const minLengthRegex = /^.{8,}/g;
  const hasLowerUpperCaseRegex = /^(?=.*[a-z])(?=.*[A-Z]).*$/g;
  const combinedRegex = /^(?!.*\s)(?=.*\d)(?=.*[^a-zA-Z0-9\s])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

  const tests = {
    minLength: minLengthRegex.test(control.value),
    hasLowerUpperCase: hasLowerUpperCaseRegex.test(control.value),
    hasNumberSpecial: hasNumberSpecialRegex.test(control.value),
    hasAll: combinedRegex.test(control.value),
  };
  const errors = {};
  Object.keys(tests)
    .filter(key => tests[key] === false)
    .forEach(key => {
      errors[key] = true;
    });
  return Object.values(errors).length > 0 ? errors : null;
}

export function matchingPasswordControls(control: FormControl): ValidationErrors | null {
  const parentFormGroup = control?.parent;

  if (parentFormGroup?.get('password').value !== control?.value) {
    return { isNotMatching: true };
  }
  return null;
}

export function customCountryCodePhoneValidator(control: FormControl): ValidationErrors | null {
  if (control && (control.value === '' || control.value === null)) return null;
  if (/\s/gi.test(control.value)) {
    control.patchValue(control.value.replace(/\s/g, ''));
  }
  return /^\+{0,1}[0-9/\-\s]+$/g.test(control.value) ? null : { countryCode: true };
}

export function customEmailValidator(control: FormControl): ValidationErrors | null {
  if (control && (control.value === '' || control.value === null)) return null;
  if (/\s/gi.test(control.value)) {
    control.patchValue(control.value.replace(/\s/g, ''));
  }
  return Validators.email(control);
}

export function customPhoneValidator(control: FormControl): ValidationErrors | null {
  if (control && (control.value === '' || control.value === null)) return null;
  if (/\s/gi.test(control.value)) {
    control.patchValue(control.value.replace(/\s/g, ''));
  }
  return /^\+{0,1}[0-9/\-\s]+$/g.test(control.value) ? null : { phone: true };
}

export function lettersOnlyValidator(control: FormControl): ValidationErrors | null {
  if (control && (control.value === '' || control.value === null)) return null;
  if (/\s/gi.test(control.value)) {
    control.patchValue(control.value.replace(/\s/g, ''));
  }
  return /^[a-zA-Z\s]*$/g.test(control.value) ? null : { onlyLetters: true };
}

export function alphaNumericValidator(control: FormControl): ValidationErrors | null {
  if (control && (control.value === '' || control.value === null)) return null;
  if (/\s/gi.test(control.value)) {
    control.patchValue(control.value.replace(/\s/g, ''));
  }
  return /^[a-zA-Z0-9\s]*$/g.test(control.value) ? null : { alphaNumeric: true };
}

export function numbersOnlyValidator(control: FormControl): ValidationErrors | null {
  if (control && (control.value === '' || control.value === null)) return null;
  if (/\s/gi.test(control.value)) {
    control.patchValue(control.value.replace(/\s/g, ''));
  }
  return /^[0-9]*$/g.test(control.value) ? null : { onlyNumbers: true };
}

// does not allow a return/enter key to be pressed as valid
export function noBlankSpaceValidator(control: AbstractControl): ValidationErrors | null {
  if (control.value && control.value.trim() === '') {
    return { whitespace: true };
  }
  return null;
}

export function minNumberOfElementsInArray(min = 1): ValidatorFn {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (control: AbstractControl): { [key: string]: any } | null => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const array = control.value as any[];
    if (array.length < min) {
      return { minNumberOfElementsRequired: true };
    }
    return null;
  };
}

export function patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (control: AbstractControl): { [key: string]: any } => {
    if (!control.value) {
      // if control is empty return no error
      return null;
    }

    // test the value of the control against the regexp supplied
    const valid = regex.test(control.value);

    // if true, return no error (no error), else return error passed in the second parameter
    return valid ? null : error;
  };
}

export function phoneNumberValidator(): ValidatorFn {
  return patternValidator(/^[0-9]*$/, { invalidPhoneNumber: true });
}

export function onlyNumbersValidator(): ValidatorFn {
  return patternValidator(/^[0-9]*$/, { onlyNumbers: true });
}

// only numbers and +
export function phonePrefixValidator(): ValidatorFn {
  return patternValidator(/^[0-9+]+$/, { invalidPhonePrefix: true });
}

// only numbers
export function zipCodeValidator(): ValidatorFn {
  return patternValidator(/^[0-9]*$/, { invalidZipcode: true });
}

// only letters, german umlauts and -
export function nameValidator(): ValidatorFn {
  return patternValidator(/^[A-Za-zäöüÄÖÜß]+([ '-][A-Za-zäöüÄÖÜß]+)*$/, { invalidName: true });
}

export function emailPatternValidator(): ValidatorFn {
  const emailRegex = new RegExp('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$');
  return patternValidator(emailRegex, {
    invalidEmail: true,
  });
}

export function ibanValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null;
    }

    const normalizedIban = value.replace(/\s+/g, '').toUpperCase();

    const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;
    if (!ibanRegex.test(normalizedIban)) {
      return { invalidIban: true };
    }

    const rearranged = normalizedIban.slice(4) + normalizedIban.slice(0, 4);

    const numericIban = rearranged
      .split('')
      .map(ch => (ch >= 'A' && ch <= 'Z' ? (ch.charCodeAt(0) - 55).toString() : ch))
      .join('');

    let remainder = '';
    for (let i = 0; i < numericIban.length; i += 7) {
      remainder = (parseInt(remainder + numericIban.substr(i, 7), 10) % 97).toString();
    }

    if (parseInt(remainder, 10) !== 1) {
      return { invalidIban: true };
    }

    return null;
  };
}

export function passwordConfirmationValidator(control: FormControl): ValidationErrors | null {
  if (!control || !control.value) {
    return null;
  }

  // Get the parent form group
  const parent = control.parent;
  if (!parent) {
    return null;
  }

  // Get the password control
  const passwordControl = parent.get('password');
  if (!passwordControl) {
    return null;
  }

  // Compare the values
  if (control.value !== passwordControl.value) {
    return { passwordMismatch: true };
  }

  return null;
}
